import { useEffect, useState } from 'react'
import { getSlaInfo, formatDuration, SLA_META } from '../../utils/sla'
import { getPriorityMeta } from '../../utils/priorityMatrix'
import { getTicketType } from '../../utils/ticketType'

/**
 * Formatea una fecha objetivo de SLA de forma corta.
 * @param {Date|null} date
 * @returns {string}
 */
const formatTarget = (date) =>
  date
    ? date.toLocaleString('es-CR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

/**
 * Tarjeta de SLA para la vista de un ticket. Muestra el objetivo de resolución
 * (con cuenta regresiva o resultado) y el de primera respuesta, con colores
 * según el cumplimiento. Se actualiza cada minuto mientras el ticket está abierto.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.ticket - Ticket con campos de SLA.
 * @returns {JSX.Element|null}
 */
const SlaCard = ({ ticket }) => {
  const [now, setNow] = useState(() => Date.now())

  // Cuenta regresiva en vivo solo si el ticket sigue abierto
  useEffect(() => {
    if (ticket?.completed) return
    const interval = setInterval(() => setNow(Date.now()), 60000)
    return () => clearInterval(interval)
  }, [ticket?.completed])

  if (!ticket?.createdAt) return null

  const { hasSla, response, resolution, overall } = getSlaInfo(ticket, now)
  const meta = SLA_META[overall] || SLA_META.pending

  // Tipo sin SLA (p. ej. Cambios): tarjeta informativa.
  if (!hasSla) {
    return (
      <div className="sla-card">
        <div className="d-flex align-items-center justify-content-between">
          <span className="sla-title">SLA</span>
          <span
            className="sla-chip"
            style={{ color: SLA_META.none.color, background: '#94a3b81a' }}
          >
            <i className={`fa-solid ${SLA_META.none.icon} me-1`} />
            Sin SLA
          </span>
        </div>
        <div className="sla-sub mt-1">
          Los tickets de tipo «{getTicketType(ticket.type).label}» no tienen SLA
          reactivo.
        </div>
        <style>{`
          .sla-title { font-size: 0.72rem; color: var(--gm-text-muted); }
          .sla-chip { font-size: 0.68rem; font-weight: 600; padding: 1px 8px; border-radius: 99px; white-space: nowrap; }
          .sla-sub { font-size: 0.72rem; color: var(--gm-text-muted); }
        `}</style>
      </div>
    )
  }

  const priorityLabel = getPriorityMeta(ticket.priorityId).label

  // Progreso de la resolución (0–100), acotado
  const createdStr = String(ticket.createdAt)
  const createdMs = new Date(
    createdStr.endsWith('Z') || createdStr.includes('+')
      ? createdStr
      : createdStr + 'Z'
  ).getTime()
  const total = resolution.target ? resolution.target.getTime() - createdMs : 0
  const elapsed = total > 0 ? total - resolution.remainingMs : 0
  const progress =
    total > 0 ? Math.min(100, Math.max(0, (elapsed / total) * 100)) : 0

  // Texto principal de la resolución
  let headline
  if (resolution.done) {
    headline =
      resolution.status === 'met'
        ? 'Resuelto a tiempo'
        : `Resuelto con retraso (${formatDuration(resolution.remainingMs)})`
  } else if (resolution.status === 'breached') {
    headline = `Vencido hace ${formatDuration(resolution.remainingMs)}`
  } else {
    headline = `Vence en ${formatDuration(resolution.remainingMs)}`
  }

  const responseMeta = SLA_META[response.status] || SLA_META.pending

  return (
    <div className="sla-card">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <span className="sla-title">SLA · {priorityLabel}</span>
        <span
          className="sla-chip"
          style={{ color: meta.color, background: `${meta.color}1a` }}
        >
          <i className={`fa-solid ${meta.icon} me-1`} />
          {meta.label}
        </span>
      </div>

      {/* Resolución */}
      <div className="sla-headline" style={{ color: meta.color }}>
        {headline}
      </div>
      <div className="sla-sub">
        Objetivo de resolución: {formatTarget(resolution.target)}
      </div>

      {!resolution.done && (
        <div className="sla-bar mt-2">
          <div
            className="sla-bar-fill"
            style={{ width: `${progress}%`, background: meta.color }}
          />
        </div>
      )}

      {/* Primera respuesta */}
      <div className="sla-response mt-2">
        <span className="sla-response-label">Primera respuesta</span>
        <span
          className="sla-response-status"
          style={{ color: responseMeta.color }}
        >
          <i className={`fa-solid ${responseMeta.icon} me-1`} />
          {response.done
            ? responseMeta.label
            : response.status === 'breached'
              ? `Vencida hace ${formatDuration(response.remainingMs)}`
              : `Vence en ${formatDuration(response.remainingMs)}`}
        </span>
      </div>

      <style>{`
        .sla-title {
          font-size: 0.72rem;
          color: var(--gm-text-muted);
        }
        .sla-chip {
          font-size: 0.68rem;
          font-weight: 600;
          padding: 1px 8px;
          border-radius: 99px;
          white-space: nowrap;
        }
        .sla-headline {
          font-size: 0.92rem;
          font-weight: 600;
          line-height: 1.2;
        }
        .sla-sub {
          font-size: 0.72rem;
          color: var(--gm-text-muted);
          margin-top: 2px;
        }
        .sla-bar {
          height: 4px;
          border-radius: 99px;
          background: var(--gm-surface-2);
          overflow: hidden;
        }
        .sla-bar-fill {
          height: 100%;
          border-radius: 99px;
          transition: width 0.4s ease;
        }
        .sla-response {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 6px;
          border-top: 1px dashed var(--gm-border);
        }
        .sla-response-label {
          font-size: 0.72rem;
          color: var(--gm-text-muted);
        }
        .sla-response-status {
          font-size: 0.72rem;
          font-weight: 600;
        }
      `}</style>
    </div>
  )
}

export default SlaCard
